import { Link } from "react-router-dom";
import {
    Calculator,
    Receipt,
    FileSpreadsheet,
    Users,
    ShieldCheck,
    Building2,
    Calendar,
    Globe2,
    HandshakeIcon,
    Clock,
    MapPin,
    Phone,
    Mail,
    ArrowRight,
} from "lucide-react";
import SEO from "../components/SEO";

const services = [
    {
        icon: Calculator,
        title: "Comptabilité",
        desc: "Tenue de comptabilité, bouclements annuels, états financiers selon les normes suisses.",
        href: "/comptabilite",
    },
    {
        icon: Receipt,
        title: "Fiscalité",
        desc: "Déclarations d'impôts, conseil fiscal pour particuliers et sociétés, optimisation légitime.",
        href: "/fiscalite",
    },
    {
        icon: FileSpreadsheet,
        title: "TVA",
        desc: "Décomptes TVA, conseil et représentation fiscale pour entreprises suisses et étrangères.",
        href: "/tva",
    },
    {
        icon: Users,
        title: "Salaires & RH",
        desc: "Gestion de paie, charges sociales, permis de travail, conformité administrative.",
        href: "/salaires-rh",
    },
    {
        icon: ShieldCheck,
        title: "Administrateur & gérant",
        desc: "Mise à disposition d'administrateur ou gérant pour sociétés suisses détenues par des actionnaires étrangers ou nécessitant une représentation locale.",
        href: "/administrateur-gerant",
    },
    {
        icon: Building2,
        title: "Création de société",
        desc: "SA, Sàrl, holdings, conseil sur la structure et accompagnement administratif complet.",
        href: "/creation-societe",
    },
];

const Home = () => {
    return (
        <>
            <SEO
                title="Fiduciaire Bleich — Cabinet fiduciaire à Genève depuis 2004"
                description="Fiduciaire indépendante à Genève. Comptabilité, fiscalité, TVA, salaires, création de société. Rue de Rive 8, depuis 2004."
                canonical="https://fiduciaire-bleich.ch/"
            />

            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative bg-white overflow-hidden"
            >
                <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, #2C3E45 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />
                <div className="container-prose relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 py-20 lg:py-32 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 mb-8 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Cabinet fiduciaire indépendant
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#2C3E45] leading-[1.05] tracking-tight">
                            Fiduciaire à Genève
                            <br />
                            <span className="italic text-[#1C9CAC]">depuis 2004.</span>
                        </h1>
                        <p className="mt-8 text-lg sm:text-xl text-[#5C6B70] max-w-2xl leading-relaxed">
                            Comptabilité, fiscalité et conseil pour entrepreneurs, indépendants
                            et particuliers. Rue de Rive 8.
                        </p>
                        <p className="mt-4 text-base italic text-[#5C6B70]/80">
                            Premier entretien gratuit et confidentiel.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-primary"
                                className="btn-primary"
                            >
                                Prendre rendez-vous
                                <ArrowRight size={16} strokeWidth={1.75} />
                            </Link>
                            <a
                                href="#services"
                                data-testid="hero-cta-secondary"
                                className="btn-secondary"
                            >
                                Découvrir nos services
                            </a>
                        </div>
                        <div
                            data-testid="hero-trust-signals"
                            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] text-[#5C6B70]"
                        >
                            <span className="inline-flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#1C9CAC]" />
                                À Genève depuis 2004
                            </span>
                            <span className="hidden sm:inline-block w-px h-3 bg-[#E5E7EB]" />
                            <span className="inline-flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#1C9CAC]" />
                                IDE CHE-112.231.914
                            </span>
                        </div>
                    </div>

                    <div className="lg:col-span-5 hidden lg:block">
                        <div className="relative">
                            <div className="absolute -inset-4 border border-[#1C9CAC]/20" />
                            <img
                                src="https://images.unsplash.com/photo-1584200463394-bba4b2117e4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxnZW5ldmElMjBzd2l0emVybGFuZCUyMGxha2V8ZW58MHx8fHwxNzc5ODkzMzg5fDA&ixlib=rb-4.1.0&q=85"
                                alt="Genève, lieu d'implantation du cabinet Fiduciaire Bleich depuis 2004"
                                className="relative w-full h-[520px] object-cover"
                                loading="eager"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* À PROPOS */}
            <section
                id="apropos"
                data-testid="about-section"
                className="bg-white py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            À propos
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Une fiduciaire indépendante
                            <br />à <span className="italic">Genève.</span>
                        </h2>
                        <div className="mt-8 space-y-6 text-[#5C6B70] text-lg leading-[1.75] max-w-2xl">
                            <p>
                                La Fiduciaire Bleich accompagne depuis plus de vingt ans les
                                entrepreneurs, indépendants, sociétés et particuliers à Genève.
                                Notre approche est simple : travailler avec rigueur, dans la
                                durée, et avec les mêmes interlocuteurs d'année en année.
                            </p>
                            <p>
                                Établis Rue de Rive depuis notre création en 2004, nous couvrons
                                l'ensemble des disciplines fiduciaires — de la comptabilité
                                courante au conseil fiscal le plus pointu. Nous travaillons en
                                français et en anglais, pour une clientèle locale comme
                                internationale.
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-5 flex flex-col gap-4 lg:pt-16">
                        {[
                            {
                                icon: Calendar,
                                title: "Depuis 2004",
                                desc: "Plus de vingt ans de pratique continue.",
                            },
                            {
                                icon: HandshakeIcon,
                                title: "Indépendante",
                                desc: "Maison familiale, sans actionnaire externe.",
                            },
                            {
                                icon: Globe2,
                                title: "Bilingue",
                                desc: "Français et anglais au quotidien.",
                            },
                        ].map(({ icon: Icon, title, desc }) => (
                            <div
                                key={title}
                                data-testid={`feature-pill-${title.toLowerCase().replace(/\s+/g, "-")}`}
                                className="flex items-start gap-5 p-6 border border-[#E5E7EB] bg-[#FAFAF7] hover:border-[#1C9CAC] transition-colors"
                            >
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white border border-[#1C9CAC]/30 text-[#1C9CAC]">
                                    <Icon size={18} strokeWidth={1.5} />
                                </div>
                                <div>
                                    <h3 className="font-serif text-xl text-[#2C3E45] mb-1">
                                        {title}
                                    </h3>
                                    <p className="text-sm text-[#5C6B70] leading-relaxed">
                                        {desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section
                id="services"
                data-testid="services-section"
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Nos services
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Six domaines d'expertise,
                            <br />
                            <span className="italic">traités avec la même rigueur.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
                        {services.map(({ icon: Icon, title, desc, href }, idx) => (
                            <Link
                                key={title}
                                to={href}
                                data-testid={`service-card-${idx}`}
                                className="bg-white p-8 lg:p-10 flex flex-col gap-4 group hover:bg-[#FAFAF7] hover:shadow-[inset_0_0_0_1px_#1C9CAC] transition-all"
                            >
                                <div className="w-11 h-11 flex items-center justify-center border border-[#1C9CAC]/30 text-[#1C9CAC] group-hover:bg-[#1C9CAC] group-hover:text-white transition-colors">
                                    <Icon size={20} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-2xl text-[#2C3E45] mt-2">
                                    {title}
                                </h3>
                                <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                    {desc}
                                </p>
                                <span
                                    data-testid={`service-card-cta-${idx}`}
                                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#1C9CAC] group-hover:gap-2.5 transition-all"
                                >
                                    En savoir plus
                                    <ArrowRight size={14} strokeWidth={2} />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* POURQUOI NOUS */}
            <section
                data-testid="why-section"
                className="bg-white py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Pourquoi nous choisir
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            La continuité d'un cabinet
                            <br />à <span className="italic">taille humaine.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
                        {[
                            {
                                title: "Un interlocuteur stable",
                                desc: "La personne qui suit votre dossier cette année le suivra encore dans cinq ans. Pas de turn-over, pas de réorganisation. La continuité dont vos affaires ont besoin.",
                            },
                            {
                                title: "Une expertise complète",
                                desc: "De la tenue de comptabilité aux questions fiscales complexes, en passant par la création de sociétés et la révision. Un seul interlocuteur pour l'ensemble.",
                            },
                            {
                                title: "Un cabinet à taille humaine",
                                desc: "Nous sommes une fiduciaire indépendante, pas un grand groupe. Cela signifie un service personnalisé, des décisions rapides, et une accessibilité réelle de la direction.",
                            },
                        ].map((item, idx) => (
                            <div
                                key={item.title}
                                data-testid={`why-column-${idx}`}
                                className="border-t border-[#1C9CAC] pt-8"
                            >
                                <h3 className="font-serif text-2xl text-[#2C3E45] mb-4 leading-snug">
                                    {item.title}
                                </h3>
                                <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT RAPIDE */}
            <section
                data-testid="quick-contact-section"
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-12">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Premier contact
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Parlons de votre <span className="italic">situation.</span>
                        </h2>
                        <p className="mt-6 text-lg text-[#5C6B70] leading-relaxed">
                            Un premier entretien — gratuit et confidentiel — permet d'évaluer
                            comment nous pouvons vous être utile. Sans engagement.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
                        <div className="bg-white p-10 space-y-5">
                            <div className="flex items-start gap-4">
                                <MapPin size={18} className="text-[#1C9CAC] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                <p className="text-[#2C3E45]">Rue de Rive 8, 1204 Genève</p>
                            </div>
                            <div className="flex items-start gap-4">
                                <Phone size={18} className="text-[#1C9CAC] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                <a
                                    href="tel:+41223193440"
                                    data-testid="quick-contact-phone"
                                    className="text-[#2C3E45] hover:text-[#1C9CAC] transition-colors"
                                >
                                    +41 22 319 34 40
                                </a>
                            </div>
                            <div className="flex items-start gap-4">
                                <Mail size={18} className="text-[#1C9CAC] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                <a
                                    href="mailto:info@fiduciaire-bleich.ch"
                                    data-testid="quick-contact-email"
                                    className="text-[#2C3E45] hover:text-[#1C9CAC] transition-colors"
                                >
                                    info@fiduciaire-bleich.ch
                                </a>
                            </div>
                            <div className="flex items-start gap-4">
                                <Clock size={18} className="text-[#1C9CAC] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                <p className="text-[#2C3E45]">Lundi–vendredi, 9h00 – 18h00</p>
                            </div>
                        </div>

                        <div className="bg-white p-10 flex flex-col justify-center items-start gap-6">
                            <p className="font-serif text-2xl text-[#2C3E45] leading-snug max-w-sm">
                                Prêt à échanger sur vos besoins ?
                            </p>
                            <Link
                                to="/contact"
                                data-testid="quick-contact-cta"
                                className="btn-primary"
                            >
                                Prendre rendez-vous
                                <ArrowRight size={16} strokeWidth={1.75} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Home;
